import { useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import { customerSchema } from "../../validation/userSchemas";

import ProInput from "../form/Input";
import TractorCard from "./TractorCard";
import MapPickerModal from "../map/MapPickerModal";
import { registerUser } from "../../api/authApi";

const emptyTractor = {
  brand: "",
  model: "",
  modelNumber: "",
  serial: "",
  year: "",
  engineHours: "",
  engineType: "",
  horsePower: "",
  transmission: "",
};

export default function AddCustomerForm({ onClose, onSuccess }) {
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (values) => {
    setServerError("");
    setSuccessMessage("");
    setLoading(true);

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phone,
      userType: "CUSTOMER",
      roles: ["FUNDI_CUSTOMER"],
      location: values.location,
      tractors: values.tractors.map((t) => ({
        brand: t.brand,
        model: t.model,
        serial: t.serial,
        year: Number(t.year),
        engineHours: Number(t.engineHours),
        engineType: t.engineType,
        horsePower: Number(t.horsePower),
        transmission: t.transmission,
      })),
    };

    try {
      const result = await registerUser(payload);

      if (result.success) {
        setSuccessMessage("Customer registered successfully!");

        setTimeout(() => {
          onSuccess?.("Customer registered successfully!");
          onClose?.();
        }, 1200);
      } else {
        setServerError(result.error?.message || "Failed to register customer.");
      }
    } catch (error) {
      setServerError("Something went wrong while registering customer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-3xl bg-black/45 backdrop-blur-sm">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/80 border-t-transparent" />
          <p className="mt-3 text-sm font-medium text-white">
            Saving customer...
          </p>
        </div>
      )}

      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          location: null,
          tractors: [emptyTractor],
        }}
        validationSchema={customerSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <>
            <Form className="space-y-6">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_35px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.35)]">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                      Add Customer
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Register a customer and capture their tractors and service
                      location.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => !loading && onClose?.()}
                    disabled={loading}
                    aria-label="Close"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                  >
                    ×
                  </button>
                </div>

                {/* Body */}
                <div className="space-y-6 p-6">
                  {successMessage && (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                      {successMessage}
                    </div>
                  )}

                  {serverError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                      {serverError}
                    </div>
                  )}

                  {/* Customer info */}
                  <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/30">
                    <div className="mb-5">
                      <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                        Customer Information
                      </h3>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Capture the customer’s profile and contact details.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      <Field
                        name="firstName"
                        label="First Name"
                        icon="👤"
                        component={ProInput}
                      />
                      <Field
                        name="lastName"
                        label="Last Name"
                        icon="👤"
                        component={ProInput}
                      />
                      <Field
                        name="email"
                        label="Email"
                        icon="📧"
                        component={ProInput}
                      />
                      <Field
                        name="phone"
                        label="Phone Number"
                        icon="📱"
                        component={ProInput}
                      />

                      <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Location
                        </label>

                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => !loading && setShowMap(true)}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && !loading) {
                              setShowMap(true);
                            }
                          }}
                          className={`group cursor-pointer rounded-2xl border px-4 py-3 transition ${
                            touched.location && errors.location
                              ? "border-red-400 bg-red-50 dark:border-red-500/50 dark:bg-red-500/10"
                              : "border-slate-200 bg-white hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-500"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-base dark:bg-slate-800">
                                📍
                              </div>

                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-900 dark:text-white">
                                  {values.location
                                    ? `${values.location.lat.toFixed(5)}, ${values.location.lng.toFixed(5)}`
                                    : "Select customer location"}
                                </p>
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                  {values.location
                                    ? "Click to update the selected coordinates"
                                    : "Open the map picker to set location"}
                                </p>
                              </div>
                            </div>

                            <span className="shrink-0 text-sm font-medium text-slate-700 dark:text-slate-300">
                              {values.location ? "Change" : "Open Map"}
                            </span>
                          </div>
                        </div>

                        {touched.location && errors.location && (
                          <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                            Location is required
                          </p>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Tractors */}
                  <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="mb-5 flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Tractors
                        </h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          Add one or more tractors owned by this customer.
                        </p>
                      </div>

                      <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {values.tractors.length}{" "}
                        {values.tractors.length === 1 ? "tractor" : "tractors"}
                      </div>
                    </div>

                    <FieldArray
                      name="tractors"
                      render={(arrayHelpers) => (
                        <div className="space-y-5">
                          {values.tractors.map((_, index) => (
                            <div
                              key={index}
                              className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-950/30"
                            >
                              <div className="mb-4 flex items-center justify-between gap-4">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                  Tractor {index + 1}
                                </div>

                                {values.tractors.length > 1 && (
                                  <button
                                    type="button"
                                    disabled={loading}
                                    onClick={() => arrayHelpers.remove(index)}
                                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-500/10"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>

                              <TractorCard
                                index={index}
                                remove={arrayHelpers.remove}
                              />
                            </div>
                          ))}

                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => arrayHelpers.push({ ...emptyTractor })}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          >
                            <span className="text-base leading-none">＋</span>
                            Add another tractor
                          </button>
                        </div>
                      )}
                    />
                  </section>
                </div>

                {/* Footer */}
                <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end dark:border-slate-800">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => !loading && onClose?.()}
                    className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black"
                  >
                    {loading && (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent dark:border-black/70 dark:border-t-transparent" />
                    )}
                    {loading ? "Saving..." : "Save Customer"}
                  </button>
                </div>
              </div>
            </Form>

            {showMap && !loading && (
              <MapPickerModal
                onSelect={(coords) => {
                  setFieldValue("location", coords);
                  setShowMap(false);
                }}
                onClose={() => setShowMap(false)}
              />
            )}
          </>
        )}
      </Formik>
    </div>
  );
}